import { computed, observable } from 'mobx';

export type SyncStatus = 'synchronized' | 'pending-insert' | 'pending-update' | 'pending-delete' | 'pending-insert-deleted';

export class SyncObject<T extends object> {
  @observable protected _syncStatus: SyncStatus;
  @observable protected _original?: T;
  @observable protected _proposed?: T;
  @observable protected _pendingEditsCount: number;

  private constructor(syncStatus: SyncStatus, original?: T, proposed?: T, pendingEditsCount?) {
    this._syncStatus = syncStatus;
    this._original = original;
    this._proposed = proposed;
    this._pendingEditsCount = pendingEditsCount ?? 0;
  }

  public static constructSynchronized<T extends object>(original: T): SyncObject<T> {
    return new SyncObject<T>('synchronized', original, undefined);
  }

  public static constructInserted<T extends object>(proposed: T): SyncObject<T> {
    return new SyncObject<T>('pending-insert', undefined, proposed, 1);
  }

  public static constructUpdated<T extends object>(original: T, proposed: T): SyncObject<T> {
    return new SyncObject<T>('pending-update', original, proposed);
  }

  public static constructDeleted<T extends object>(original: T): SyncObject<T> {
    return new SyncObject<T>('pending-delete', original, undefined);
  }

  @computed get syncStatus(): SyncStatus {
    return this._syncStatus;
  }

  @computed get deletedObject(): T | undefined {
    switch (this._syncStatus) {
      case 'pending-delete':
        return this._original;
      default:
        throw new Error('Unexpected syncStatus.');
    }
  }

  @computed get current(): T | undefined {
    switch (this._syncStatus) {
      case 'synchronized':
        return this._original;

      case 'pending-insert':
        return this._proposed;

      case 'pending-insert-deleted':
        return this._proposed;

      case 'pending-delete':
        throw new Error('Cannot access deleted state.');

      case 'pending-update':
        return this._proposed;

      default:
        throw new Error('Unexpected syncStatus.');
    }
  }

  get original(): T | undefined {
    return this._original;
  }

  get pendingEditsCount(): number {
    return this._pendingEditsCount;
  }

  delete() {
    if (this._syncStatus === 'synchronized') {
      this._proposed = undefined;
      this._syncStatus = 'pending-delete';
      return;
    }
    if (this._syncStatus === 'pending-update') {
      this._proposed = undefined;
      this._syncStatus = 'pending-delete';
      return;
    }
    if (this._syncStatus === 'pending-insert') {
      this._proposed = undefined;
      this._syncStatus = 'pending-insert-deleted';
    }
    if (this._syncStatus === 'pending-delete') {
      throw new Error('Invalid operation. Already deleted.');
    }
  }

  update(value: T) {
    if (this._syncStatus === 'pending-delete') {
      if (value === this._original) {
        this._proposed = undefined;
        this._syncStatus = 'synchronized';
        return;
      } else {
        this._proposed = value;
        this._syncStatus = 'pending-update';
        return;
      }
    }

    if (this._syncStatus === 'pending-insert') {
      this._proposed = value;
      return;
    }

    if (this._syncStatus === 'pending-insert-deleted') { // For undoing deletion of an inserted syncObject
      this._proposed = value;
      this._syncStatus = 'pending-insert';
      return;
    }

    if (this._syncStatus === 'synchronized') {
      this._proposed = value;
      this._syncStatus = 'pending-update';
      return;
    }

    if (this._syncStatus === 'pending-update') {
      if (value === this._original) {
        this._proposed = undefined;
        this._syncStatus = 'synchronized';
        return;
      } else {
        this._proposed = value;
        return;
      }
    }
  }

  updateEditCount(mode: 'add' | 'subtract') {
    if (mode === 'add') {
      this._pendingEditsCount += 1;
    } else {
      if (this._pendingEditsCount > 0) this._pendingEditsCount -= 1;
    }
  }

  clearPendingEditCount() {
    this._pendingEditsCount = 0;
  }

  // hasChanges(): boolean {
  //   throw new Error('Not implemented.');
  // }

  // rejectChanges(): void {
  //   throw new Error('Not implemented.');
  // }

  acceptChanges(): void {
    if (this._syncStatus === 'pending-update' || this._syncStatus === 'pending-insert') {
      this._original = this._proposed;
      this._syncStatus = 'synchronized';
    }
  }
}
